import { useMutation } from "@tanstack/react-query";
import { useSetRecoilState } from "recoil";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { useNavigate } from "react-router-dom";
import {
  axiosInstancePromotion,
  CustomResponseFormat,
  setBearerTokenPromotion,
} from "api/instancePromotion";
import { syncFbcToAccount, trackCapiStandardEvent } from "analytics/metaCapi";
import { trackXAdsEvent } from "analytics/xAdsPixel";
import { getStoredChannelParams } from "analytics/channelParams";
import mixpanel from "mixpanel-browser";
import useMixpanelProfileMkt from "./useMixpanelProfileMkt";
import useMixpanelMKT from "./useMixpanelMKT";
import useGtagConversion from "./useGtagConversion";
import { useState } from "react";
import { webFunnelApi } from "../api/webFunnelApi";
import { captureException } from "../utils/sentry";
import useSaveReferralCode from "./useSaveReferralCode";
import { PATH } from "../routes/paths";
import { getFunnelPaths } from "routes/adFunnel";

type SocialType = "google" | "apple" | "kakao";

interface LoginVars {
  idToken: string;
  email?: string;
}

export default function useLoginPromotionMutation(
  type: SocialType,
  // 제휴(레퍼럴) 진입일 때의 코드. 프로모션 퍼널에서는 항상 undefined 다.
  referralCode?: string
) {
  const navigate = useNavigate();
  const setUser = useSetRecoilState(userSimpleAtom);
  const saveReferralCode = useSaveReferralCode();
  const setPromotionPeople = useMixpanelProfileMkt("v4");
  const trackGtagEvent = useGtagConversion();
  const trackMixpanelRegistration = useMixpanelMKT({
    params: {
      method: type,
    },
    eventName: "가입",
    version: "v4",
  });
  const trackMixpanelLogin = useMixpanelMKT({
    params: {
      method: type,
    },
    eventName: "로그인",
    version: "v4",
  });
  const [error, setError] = useState<string>();

  const { mutate: identify } = useMutation(
    ({ distinctId }: { distinctId: string }) =>
      webFunnelApi.identify(distinctId),
    {
      retry: 2,
      onError: (error, variables) => {
        captureException(error, {
          tags: { action: "web_funnel_identify" },
          extra: { distinctId: variables.distinctId },
        });
      },
    }
  );

  const mutation = useMutation({
    mutationFn: async ({ idToken, email }: LoginVars) => {
      // 서버가 로그인 처리 중 웹퍼널 데이터를 연결하도록 clientId 를 함께 보낸다
      // (별도 /web-funnel/identify 는 fire-and-forget 이라 유실될 수 있다)
      const distinctId =
        localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ||
        (mixpanel as any).promotion?.get_distinct_id();

      const response = await axiosInstancePromotion.post<CustomResponseFormat>(
        `/user/v2/login`,
        {
          idToken,
          type,
          ...(email && { email }),
          ...(distinctId && { clientId: distinctId }),
          // 제휴 진입(레퍼럴 코드 보유) 시 신규 가입을 앱 온보딩 유저로 등록(isWebFunnelRegister=false)
          ...(referralCode && {
            isAffiliate: true,
          }),
        }
      );
      return response;
    },
    onSuccess: async (response) => {
      if (response.code === 0) {
        const result: any = response.result;

        setBearerTokenPromotion(result.accessToken);
        setUser(result);

        // 제휴 진입이면 레퍼럴 코드를 로그인된 유저에 영속화 (last-touch).
        // 결과 화면이 최신 referralCodeType 을 읽도록 유저 정보 갱신까지 마친 뒤 진행한다.
        // 일반 프로모션 로그인은 코드가 없으므로 no-op.
        if (referralCode) {
          await saveReferralCode(referralCode);
        }

        const distinctId =
          localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ||
          (mixpanel as any).promotion?.get_distinct_id();

        if (distinctId) {
          identify({ distinctId: distinctId });
        }

        if (result.isFirstLogin) {
          // Meta CAPI
          trackCapiStandardEvent("CompleteRegistration", {
            value: 0,
            currency: "KRW",
            externalId: result.userId,
            email: result.email,
            ...(referralCode && { affiliate_code: referralCode }),
          });

          // Google Ads
          trackGtagEvent("CompleteRegistration", "v4", {
            email: result.email,
          });

          // X Ads
          trackXAdsEvent("Lead", {
            value: 0,
            currency: "KRW",
            email: result.email,
            status: "completed",
          });

          // Mixpanel identify & people
          trackMixpanelRegistration();

          // 웹퍼널 유입 채널(utm/클릭 ID)을 가입 시점에 1회 서버 저장 — 프로필 채널은 first-touch라 재로그인엔 보내지 않음
          const storedChannel = getStoredChannelParams();
          if (storedChannel) {
            webFunnelApi.saveChannelParams(storedChannel).catch((e) =>
              captureException(e, {
                tags: { action: "web_funnel_channel_save" },
              })
            );
          }
        } else {
          trackMixpanelLogin();

          // 재로그인은 CAPI 이벤트가 없어 서버 저장 fbc가 갱신되지 않는다 — 최신 클릭(fbc)을 직접 동기화
          syncFbcToAccount(result.userId);
        }

        // Mixpanel identify & people
        const promotionInstance = (mixpanel as any).promotion;
        if (promotionInstance && result.trackId) {
          // promotionInstance.identify(result.trackId);

          setPromotionPeople({
            $email: result.email,
            user_id: result.userId,
            "테스트 유형": "C",
            "로그인 페이지 유형": "E",
            ...(referralCode && { "제휴 코드": referralCode }),
            ...(result.nickname && { $name: result.nickname }),
            ...(result.paymentPageType && {
              "결제 페이지 유형": result.paymentPageType.toUpperCase(),
            }),
            ...(result.pricingModel && {
              "결제 모델 유형": result.pricingModel.toUpperCase(),
            }),
          });
        }

        const funnelPaths = getFunnelPaths();
        if (referralCode) {
          // 제휴 진입 → eligibility 확인 + 앱 딥링킹/안내는 결과 화면이 담당
          navigate(PATH.referralResult, { replace: true });
        } else if (result.isFirstLogin) {
          navigate(`${funnelPaths.onboardingBase}/nickname/1`, {
            replace: true,
          });
        } else {
          if (result?.hasAlreadyUsed) {
            navigate(funnelPaths.alreadyUsed, { replace: true });
          } else {
            navigate(`${funnelPaths.onboardingBase}/auth-loading/1`, {
              replace: true,
            });
          }
        }
      } else {
        setError(`로그인에 실패했습니다. 다시 시도해주세요.: ${response.code}`);
      }
    },
    onError: (e: any) => {
      console.error("Promotion login failed:", e);
      setError(`로그인에 실패했습니다. 다시 시도해주세요.`);
    },
  });

  return {
    login: mutation.mutate,
    loginAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error,
  };
}
